import {
  Box,
  Button,
  Hidden,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ApiError } from "../../api/ApiError";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { useForm } from "react-hook-form";
import alertService from "../../components/alert/alertService";
import { useAuth } from "../../contexts/UserContext";
import { SubOrganizations } from "../../models/SubOrganization";
import SubOrganizationsService from "../../service/sub-organization.service";
import { common, input, organization } from "../../i18n";
import AutoLocationThailand from "./AutoLocationThailand";

type SubOrganizationProfileMode = "add" | "edit";

interface Props {
  mode: SubOrganizationProfileMode;
}

interface FormValues {
  name: string;
  nameEn: string;
  nameTh: string;
  address: string;
  // ── AutoLocationThailand fields ──
  postalCode: string;
  cityRefId: string;
  districtRefId: string;
  subdistrictRefId: string;
}

const SubOrganizationProfile = (props: Props) => {
  const { t } = useTranslation([common, organization, input], {
    nsMode: "fallback",
  });

  const context = useContext(OrganizationContext);
  const { isAdmin } = useAuth();
  const [locationKey, setLocationKey] = useState(0);

  if (!context) {
    throw Error();
  }

  const subOrganization = context.subOrganizations?.find((item) => {
    return item.id === context.selectedSubOrganization;
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const body: any = {
        name: values.name.trim(),
        nameEn: values.nameEn.trim(),
        nameTh: values.nameTh.trim(),
        subdistrictRef: values.subdistrictRefId,
        addressEn: values.address.trim(),
        addressTh: values.address.trim(),
      };

      let result: SubOrganizations[] | null = null;

      if (props.mode === "add") {
        result = await SubOrganizationsService.addSuborganizationsV2(body);
      } else {
        result = await SubOrganizationsService.editSubOrganizationsV2(
          body,
          subOrganization?.refId ?? "",
        );
      }

      if (result) {
        alertService.mixin(t("Successfully"));
        context.setSubOrganizations(result);
        context.setOpenModal(false);
      }
    } catch (error) {
      if (error instanceof ApiError) return;
      alertService.error(t("validation-failed"));
    }
  };

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      nameEn: "",
      nameTh: "",
      address: "",
      postalCode: "",
      cityRefId: "",
      districtRefId: "",
      subdistrictRefId: "",
    },
  });

  const checkKeyDown = (event: any) => {
    if (event.key === "Enter") event.preventDefault();
  };

  useEffect(() => {
    if (props.mode === "edit" && subOrganization) {
      reset({
        name: subOrganization.name ?? "",
        nameEn: subOrganization.nameEn ?? "",
        nameTh: subOrganization.nameTh ?? "",
        address: subOrganization.location?.address?.en ?? "",
        postalCode: "",
        cityRefId: "",
        districtRefId: "",
        subdistrictRefId: "",
      });
      setLocationKey((k) => k + 1);
    }
  }, [subOrganization]);

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
        onKeyDown={(e) => checkKeyDown(e)}
      >
        <Box
          sx={{ height: "100%", width: "100%" }}
          display={"flex"}
          flexDirection={"column"}
          gap={"40px"}
        >
          <Box flex={1} sx={{ height: "100%", width: "100%" }}>
            <Grid2
              container
              rowGap={"20px"}
              columnSpacing={"40px"}
              sx={{ width: "100%" }}
            >
              {/* ── ชื่อ Sub-Organization ── */}
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Sub-Organization-Name-Display")}
                  info={t("Sub-Organization-Name-Display")}
                  name="name"
                  placeholder={t("Sub-Organization-Name-Display")}
                  control={control}
                  defaultValue={subOrganization?.nameEn ?? ""}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Sub-Organization-Name-Display"),
                    }),
                    pattern: {
                      value: /^[^,]*$/,
                      message: t("Name-Fails-To-Match-Pattern"),
                    },
                    validate: (text: string) => {
                      const index = context?.subOrganizations?.findIndex(
                        (sub) =>
                          (sub.nameEn ?? sub.name).trim().toLowerCase() ===
                          text.trim().toLowerCase(),
                      );
                      return index !== undefined &&
                        index >= 0 &&
                        (subOrganization?.nameEn ?? subOrganization?.name) !==
                          text
                        ? t("SUBORGANIZATION_ALREADY_EXISTS")
                        : true;
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              {/* ── Sub-Organization ID (edit) / placeholder (add) ── */}
              {props.mode === "edit" ? (
                <Grid2 xs={12} md={6}>
                  <Stack gap={"8px"}>
                    <Typography variant="h3">
                      {t("sub-organizations-id")}
                    </Typography>
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{
                        height: "36px",
                        boxSizing: "border-box",
                        flexGrow: 1,
                        p: 0,
                        backgroundColor: "var(--gray5)",
                      }}
                    >
                      <Stack direction={"row"} height={"100%"}>
                        <Typography
                          alignContent={"center"}
                          sx={{ flexGrow: 1, px: "12px" }}
                        >
                          {subOrganization?.id}
                        </Typography>
                        <IconButton
                          color="primary"
                          sx={{ p: "10px" }}
                          aria-label="copy"
                          onClick={() =>
                            navigator.clipboard.writeText(
                              subOrganization?.id.toString() ?? "",
                            )
                          }
                        >
                          <img src="/img/copy.svg" alt="copy" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  </Stack>
                </Grid2>
              ) : (
                <Hidden mdDown>
                  <Grid2 xs={12} md={6} />
                </Hidden>
              )}

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Sub-Organization-Name-English")}
                  info={t("Sub-Organization-Name-English")}
                  name="nameEn"
                  placeholder={t("Sub-Organization-Name-English")}
                  control={control}
                  defaultValue=""
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Sub-Organization-Name-English"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[A-Za-z0-9\s\-_.;\/()]+$/,
                      message: t("English-Only-Allowed"),
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Sub-Organization-Name-Thai")}
                  info={t("Sub-Organization-Name-Thai")}
                  name="nameTh"
                  placeholder={t("Sub-Organization-Name-Thai")}
                  control={control}
                  defaultValue=""
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Sub-Organization-Name-Thai"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[ก-๙0-9\s\-_.;\/()]+$/,
                      message: t("Thai-Only-Allowed"),
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              {/* key = subdistrictRef + locationKey
                  - locationKey increment ทุกครั้งที่ subOrganization อัปเดต → บังคับ remount */}
              <AutoLocationThailand
                key={`${
                  subOrganization?.subdistrictRef ?? "new"
                }-${locationKey}`}
                control={control}
                setValue={setValue}
                watch={watch}
                disabled={!isAdmin()}
                initialSubdistrictRefId={
                  subOrganization?.subdistrictRef ?? undefined
                }
              />

              {/* ── Address ── */}
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Address")}
                  info={t("Address")}
                  name="address"
                  placeholder={t("Address")}
                  control={control}
                  defaultValue=""
                  rules={{}}
                  disabled={!isAdmin()}
                />
              </Grid2>
              <Grid2 xs={12} md={6} />

              <Grid2 xs={12}>
                {isAdmin() && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    {props.mode === "edit" ? t("Save") : t("Add")}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default SubOrganizationProfile;
